// modules/approvals/utils/requestDisplay.js
//
// Shared helpers for rendering "what is this approval actually for".
//
// The approval engine never branches on entity type — every business module
// registers its own inbox `columns` and a `viewIntent` in its registerWorkflows.js,
// and stamps the human-facing values onto ApprovalRequest.displayMeta. These
// helpers are the frontend half of that contract: they resolve a registered
// column key against an instance, format it for display, and turn a viewIntent
// into a route. Nothing here hardcodes an entity type except the intent map,
// which is a routing table, not business logic.

export const fmtDateTime = (d) =>
  !d
    ? '-'
    : new Date(d).toLocaleString('en-IN', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });

// camelCase / snake_case / SCREAMING_SNAKE_CASE → "Title Case"
export const prettifyKey = (key) => {
  const words = String(key)
    .replace(/[_.]/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  return words
    // Don't shout SCREAMING_SNAKE entity types back at the user.
    .map((w) => (/^[A-Z0-9]+$/.test(w) ? w.charAt(0) + w.slice(1).toLowerCase() : w))
    .join(' ')
    .replace(/^./, (c) => c.toUpperCase());
};

/**
 * Resolve a registered column key (possibly dotted, e.g. "displayMeta.amount")
 * against an approval instance. Returns a raw value — formatting is separate.
 */
export function resolveColumnValue(key, record) {
  switch (key) {
    case 'businessRef': return record.requestId?.businessRef || record.objectId || '-';
    case 'requestedBy':
      return record.requestId?.requestedBy?.name || record.createdBy?.name || '-';
    case 'createdAt': return fmtDateTime(record.createdAt || record.startedAt);
    case 'priority': return record.requestId?.priority || 'normal';
    case 'status': return record.status;
    case 'currentStep': {
      const step = record.steps?.find((s) => s.stepNumber === record.currentStep);
      return step?.stepName || '-';
    }
    default:
      if (key.startsWith('displayMeta.')) {
        const k = key.slice('displayMeta.'.length);
        const v = record.requestId?.displayMeta?.[k];
        return v === undefined || v === null || v === '' ? '-' : v;
      }
      return record[key] ?? '-';
  }
}

/** Render a resolved value as a plain string according to its declared column type. */
export function formatColumnValue(type, value) {
  if (value === undefined || value === null || value === '' || value === '-') return '-';
  if (type === 'currency') return `₹${Number(value).toLocaleString('en-IN')}`;
  if (type === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString('en-IN') : String(value);
  }
  if (type === 'datetime') {
    // Already formatted by resolveColumnValue for createdAt; format raw dates too.
    return /^\d{4}-\d{2}-\d{2}/.test(value) ? fmtDateTime(value) : String(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// Columns that describe the WORKFLOW rather than the business ask. The instance
// view already renders these (steps, status), so the details panel skips them.
const WORKFLOW_COLUMN_KEYS = new Set(['status', 'currentStep']);

/**
 * Business-facing rows for the details panel, derived from the entity type's
 * registered columns. Falls back to whatever displayMeta holds when the entity
 * type isn't registered (or /entity-types was not permitted for this user).
 */
export function buildDetailRows(record, entityMeta) {
  const cols = Array.isArray(entityMeta?.columns) ? entityMeta.columns : null;

  if (cols && cols.length) {
    return cols
      .filter((c) => !WORKFLOW_COLUMN_KEYS.has(c.key))
      .map((c) => ({
        key: c.key,
        label: c.label || prettifyKey(c.key),
        type: c.type,
        value: formatColumnValue(c.type, resolveColumnValue(c.key, record)),
        isBadge: c.type === 'badge',
      }))
      .filter((r) => r.value !== '-');
  }

  // Unregistered entity type — show the raw business metadata rather than nothing.
  const meta = record.requestId?.displayMeta || {};
  const rows = [
    {
      key: 'businessRef',
      label: 'Reference',
      value: formatColumnValue('text', resolveColumnValue('businessRef', record)),
      isBadge: false,
    },
    ...Object.entries(meta).map(([k, v]) => ({
      key: `displayMeta.${k}`,
      label: prettifyKey(k),
      value: formatColumnValue(undefined, v),
      isBadge: false,
    })),
    {
      key: 'requestedBy',
      label: 'Requested By',
      value: formatColumnValue('text', resolveColumnValue('requestedBy', record)),
      isBadge: false,
    },
    {
      key: 'createdAt',
      label: 'Requested On',
      value: resolveColumnValue('createdAt', record),
      isBadge: false,
    },
  ];
  return rows.filter((r) => r.value !== '-');
}

/** The rule-engine facts that selected this workflow, as label/value rows. */
export function buildFactRows(record) {
  const facts = record.requestId?.facts;
  if (!facts || typeof facts !== 'object') return [];
  return Object.entries(facts)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => ({ key: k, label: prettifyKey(k), value: formatColumnValue(undefined, v) }));
}

// ── viewIntent → route ────────────────────────────────────────────────────────
// Registered intents (services/**/registerWorkflows.js) mapped to the screen
// that actually shows the record. Anything unmapped returns null and the caller
// simply hides the button rather than navigating somewhere misleading.
const SECURITY_TAB_BY_ENTITY = {
  LOGIN_POLICY: 'policies',
  LOGIN_POLICY_ASSIGNMENT: 'assignment',
  DEVICE_REGISTRATION: 'devices',
  DEVICE_ASSIGNMENT: 'assignment',
  DEVICE_REMOVAL: 'devices',
  LOGIN_POLICY_IP_CHANGE: 'ips',
};

/**
 * @returns {{ path: string, label: string } | null}
 */
export function resolveViewTarget(record, entityMeta) {
  const intent = entityMeta?.viewIntent;
  if (!intent) return null;

  const entityId = record.requestId?.entityId || record.objectId;
  const meta = record.requestId?.displayMeta || {};
  const entityType = record.entityType || record.requestId?.entityType || record.module;

  switch (intent) {
    case 'OPEN_PAYMENT_SETTLEMENT':
      return {
        path: entityId ? `/treasury/payment-requests?focus=${entityId}` : '/treasury/payment-requests',
        label: 'Open Payment Request',
      };
    case 'OPEN_EOD_CLOSURE':
      return { path: '/treasury/closure-approvals', label: 'Open Day Closure' };
    case 'OPEN_PLEDGE_CASE':
    case 'OPEN_PLEDGE_DOCUMENTS':
      return meta.caseId
        ? { path: `/pledge-release-cases/${meta.caseId}?tab=documents`, label: 'Open Pledge Case' }
        : { path: '/pledge-release-cases', label: 'Open Pledge Cases' };
    case 'OPEN_CUSTOMER':
      return meta.customerId
        ? { path: `/customers/${meta.customerId}`, label: 'Open Customer' }
        : { path: '/customers', label: 'Open Customers' };
    case 'OPEN_JOURNAL_ENTRIES':
      return { path: '/accounting/journal-entries?view=actions', label: 'Open Journal Entries' };
    case 'OPEN_MANUAL_JOURNAL_ENTRIES':
      return { path: '/accounting/journal-entries?view=requests', label: 'Open Manual Entries' };
    case 'OPEN_POSTING_RULES':
      // The Change Requests tab is where the full before/after diff lives.
      return { path: '/accounting/posting-rules?view=requests', label: 'Open Posting Rules' };
    case 'OPEN_GST_CENTRE':
      return { path: '/accounting/gst-centre?tab=manual', label: 'Open GST Centre' };
    case 'OPEN_STOCK_REGISTER':
      return { path: '/accounting/stock-register', label: 'Open Stock Register' };
    case 'OPEN_LEAVE_MANAGEMENT':
      return { path: '/hr/leave', label: 'Open Leave Management' };
    case 'OPEN_ATTENDANCE_LATE_ARRIVALS':
      return { path: '/hr/attendance?tab=late', label: 'Open Late Arrivals' };
    case 'OPEN_ATTENDANCE_REGULARIZATIONS':
      return { path: '/hr/attendance?tab=daily', label: 'Open Attendance' };
    case 'OPEN_INVENTORY_TRANSFER':
      return entityId
        ? { path: `/vault/transfers/${entityId}`, label: 'Open Transfer' }
        : { path: '/vault/transfer-approvals', label: 'Open Transfers' };
    case 'OPEN_SECURITY_CHANGE_REQUESTS': {
      const tab = SECURITY_TAB_BY_ENTITY[entityType];
      return { path: tab ? `/security?tab=${tab}` : '/security', label: 'Open Security Settings' };
    }
    default:
      return null;
  }
}
